/**
 * Service Zernio — Intégration zernio.com côté frontend.
 * Communique avec les routes backend /api/v1/socials/*.
 */
import { apiClient } from './apiClient';
import type { ZernioPlatform } from '../types/channel';
import { Logger } from '../utils/logger';

// ── Types de réponse ──────────────────────────────────────────────────

export interface ZernioAccount {
  id: string;
  platform: string;
  username: string;
  avatar_url?: string;
  status: string;
}

export interface ConnectResponse {
  authorization_url: string;
  platform: string;
  // Telegram access code flow
  access_code?: string;
  bot_username?: string;
  instructions?: string[];
  expires_at?: string;
  expires_in?: number;
}

export interface TelegramCheckResponse {
  status: 'pending' | 'connected' | 'expired';
  expiresAt?: string;
  expiresIn?: number;
  chatId?: string;
  chatTitle?: string;
  chatType?: string;
  account?: {
    _id: string;
    platform: string;
    username?: string;
    displayName?: string;
  };
}

export interface AccountOption {
  id: string;
  name: string;
  type?: string;
  avatarUrl?: string;
  avatar_url?: string;
  imageUrl?: string;
  image_url?: string;
}

export interface CallbackPayload {
  platform: string;
  account_id: string;
  channel_id: string;
  username?: string;
  avatar_url?: string;
}

export interface PublishPayload {
  channel_id: string;
  content: string;
  media_urls?: string[];
  scheduled_at?: string;
}

// ── Service ───────────────────────────────────────────────────────────

export const zernioService = {
  /**
   * Crée ou récupère le profil Zernio de l'utilisateur courant.
   */
  ensureProfile: async (): Promise<{ zernio_profile_id: string }> => {
    Logger.debug('[ZernioService] Ensuring Zernio profile');
    const response = await apiClient.post<{ zernio_profile_id: string }>('/socials/profile');
    Logger.info('[ZernioService] Zernio profile ensured', { zernioProfileId: response.data.zernio_profile_id });
    return response.data;
  },

  /**
   * Liste tous les comptes sociaux connectés via Zernio.
   */
  listAccounts: async (): Promise<ZernioAccount[]> => {
    Logger.debug('[ZernioService] Fetching connected Zernio accounts');
    const response = await apiClient.get<{ accounts: ZernioAccount[] }>('/socials/accounts');
    Logger.info('[ZernioService] Connected Zernio accounts fetched', { count: response.data.accounts.length });
    return response.data.accounts;
  },

  /**
   * Obtient l'URL d'autorisation pour connecter un réseau social.
   * headless=true : Zernio retourne un connect_token au lieu de montrer sa propre sélection.
   */
  getConnectUrl: async (platform: ZernioPlatform, callbackUrl: string, channelId?: string, headless = false): Promise<ConnectResponse> => {
    Logger.info('[ZernioService] Requesting Zernio connect URL', { platform, channelId: channelId ?? null, headless });
    const response = await apiClient.post<ConnectResponse>('/socials/accounts/connect', {
      platform,
      callback_url: callbackUrl,
      channel_id: channelId,
      headless,
    });
    Logger.info('[ZernioService] Zernio connect URL received', {
      platform,
      channelId: channelId ?? null,
      hasAuthorizationUrl: !!response.data.authorization_url,
      hasAccessCode: !!response.data.access_code,
    });
    return response.data;
  },

  /**
   * Headless mode — liste les options disponibles après OAuth (orgs, pages, boards...).
   */
  listAccountOptions: async (platform: string, connectToken: string): Promise<{ options: AccountOption[]; platform: string }> => {
    Logger.info('[ZernioService] Listing headless account options', { platform });
    const response = await apiClient.post<{ options: AccountOption[]; platform: string }>('/socials/accounts/connect/list', {
      platform,
      connect_token: connectToken,
    });
    return response.data;
  },

  /**
   * Headless mode — finalise la connexion en sélectionnant une org/page/board.
   */
  selectAccountOption: async (platform: string, connectToken: string, selectionId: string): Promise<Record<string, unknown>> => {
    Logger.info('[ZernioService] Selecting headless account option', { platform, selectionId });
    const response = await apiClient.post<Record<string, unknown>>('/socials/accounts/connect/select', {
      platform,
      connect_token: connectToken,
      selection_id: selectionId,
    });
    return response.data;
  },

  /**
   * Gère le callback après autorisation Late.
   * Met à jour le channel Raiso avec les infos Late.
   */
  handleCallback: async (payload: CallbackPayload): Promise<{ status: string; channel_id: string }> => {
    Logger.info('[ZernioService] Handling Zernio callback', {
      platform: payload.platform,
      channelId: payload.channel_id,
      accountId: payload.account_id,
    });
    const response = await apiClient.post<{ status: string; channel_id: string }>(
      '/socials/accounts/callback',
      payload,
    );
    Logger.info('[ZernioService] Zernio callback handled', {
      platform: payload.platform,
      channelId: response.data.channel_id,
      status: response.data.status,
    });
    return response.data;
  },

  /**
   * Vérifie le statut de connexion Telegram via le code d'accès.
   * Polling : appel répété jusqu'à status='connected' ou 'expired'.
   */
  checkTelegramConnection: async (code: string, channelId?: string): Promise<TelegramCheckResponse> => {
    Logger.debug('[ZernioService] Checking Telegram connection', { channelId: channelId ?? null });
    const response = await apiClient.post<TelegramCheckResponse>('/socials/accounts/telegram/check', {
      code,
      channel_id: channelId,
    });
    Logger.info('[ZernioService] Telegram connection checked', {
      channelId: channelId ?? null,
      status: response.data.status,
    });
    return response.data;
  },

  /**
   * Déconnecte un compte social.
   */
  disconnectAccount: async (accountId: string): Promise<void> => {
    Logger.warn('[ZernioService] Disconnecting Zernio account', { accountId });
    await apiClient.delete(`/socials/accounts/${accountId}`);
    Logger.info('[ZernioService] Zernio account disconnected', { accountId });
  },

  /**
   * Rafraîchit les tokens d'un compte social.
   */
  refreshAccount: async (accountId: string): Promise<void> => {
    Logger.info('[ZernioService] Refreshing Zernio account', { accountId });
    await apiClient.post(`/socials/accounts/${accountId}/refresh`);
    Logger.info('[ZernioService] Zernio account refreshed', { accountId });
  },

  /**
   * Vérifie le statut d'un compte social.
   */
  getAccountStatus: async (accountId: string): Promise<{ account_id: string; status: string }> => {
    Logger.debug('[ZernioService] Fetching Zernio account status', { accountId });
    const response = await apiClient.get<{ account_id: string; status: string }>(
      `/socials/accounts/${accountId}/status`,
    );
    Logger.info('[ZernioService] Zernio account status fetched', {
      accountId: response.data.account_id,
      status: response.data.status,
    });
    return response.data;
  },

  /**
   * Publie un post via Late.
   */
  publishPost: async (payload: PublishPayload): Promise<Record<string, unknown>> => {
    Logger.info('[ZernioService] Publishing post via Late', {
      channelId: payload.channel_id,
      hasMedia: !!payload.media_urls?.length,
      isScheduled: !!payload.scheduled_at,
    });
    const response = await apiClient.post('/socials/publish', payload);
    Logger.info('[ZernioService] Post publish request sent via Zernio', { channelId: payload.channel_id });
    return response.data;
  },

  /**
   * Synchronise les comptes Zernio avec les channels Raiso.
   * Appelle POST /socials/sync pour réconcilier automatiquement.
   *
   * V4-FRONT-ZERNIO-SYNC-TOAST — `silent` : la sync est déclenchée en arrière-plan par
   * `useChannels.loadChannels` à chaque montage, et son échec y est déjà traité comme
   * non-bloquant. Sans ce flag, un 502 (Zernio down ou non configuré) traverse la branche
   * `status >= 500` de l'intercepteur et affiche un toast rouge « Erreur serveur » à
   * CHAQUE navigation — l'app paraît cassée alors que la sync est optionnelle.
   */
  syncAccounts: async (): Promise<{ synced: Array<{ channel_id: string; action: string; platform: string }> }> => {
    Logger.info('[ZernioService] Syncing Zernio accounts');
    const response = await apiClient.post<{
      synced: Array<{ channel_id: string; action: string; platform: string }>;
    }>('/socials/sync', undefined, { timeout: 10000, metadata: { silent: true } });
    Logger.info('[ZernioService] Zernio accounts synced', { count: response.data.synced.length });
    return response.data;
  },
};
